// PrivacyPolicyPage.tsx
import React from 'react';

// Define company-specific details (Update these placeholders)
const COMPANY_NAME = " Vermi Compost Barillery";
const EFFECTIVE_DATE = "November 05, 2025";
const CONTACT_EMAIL = "info@vermicompostbarillery.com";
const CONTACT_PHONE = "[+91 7830694633]";
const CONTACT_ADDRESS = "[Delhi-Lucknow Highway near Lalpur Chauraha Barielly]";

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 font-inter mt-32">
      {/* Container for the content */}
      <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-lg ">
        
        {/* Header */}
        <h1 className="text-4xl font-extrabold text-emerald-700 mb-2 border-b-4 border-emerald-100 pb-3">
          📜 Privacy Policy for {COMPANY_NAME}
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          <strong>Effective Date:</strong> {EFFECTIVE_DATE}
        </p>

        <p className="text-gray-700 mb-8 leading-relaxed">
          Your privacy is extremely important to us. This Privacy Policy outlines how {COMPANY_NAME} collects, uses, discloses, and safeguards your information when you use our website, services, and purchase our vermicomposting products.
        </p>

        {/* --- 1. Information We Collect --- */}
        <section className="mb-10 space-y-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-l-4 border-emerald-500 pl-3">
            1. Information We Collect
          </h2>
          <p className="text-gray-700">
            We collect several types of information for various purposes to provide and improve our services to you.
          </p>

          <h3 className="text-xl font-semibold text-gray-700 mt-6">A. Personally Identifiable Information (PII)</h3>
          <p className="text-gray-700">
            This is the information that can be used to identify you:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
            <li><strong>Contact Details:</strong> Name, email address, postal address (billing and shipping), and phone number.</li>
            <li><strong>Transaction Details:</strong> Records of the products or services you have purchased, order dates, and payment amounts.</li>
            <li><strong>Account Details:</strong> Your username and encrypted password, if you create an account on our website.</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-700 mt-6">B. Usage Data</h3>
          <p className="text-gray-700">
            When you access our website, we may automatically collect certain information:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
            <li>Your computer's Internet Protocol (IP) address.</li>
            <li>Browser type and version.</li>
            <li>The pages of our website that you visit, the time and date of your visit, and the time spent on those pages.</li>
          </ul>
        </section>
        
        {/* --- 2. How We Use Your Information --- */}
        <section className="mb-10 space-y-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-l-4 border-emerald-500 pl-3">
            2. How We Use Your Information
          </h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-600 uppercase tracking-wider border-b">Purpose</th>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-600 uppercase tracking-wider border-b">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-3 px-4 text-gray-800 font-medium">Order Fulfillment</td>
                  <td className="py-3 px-4 text-gray-600">To process, confirm, and deliver your orders for vermi compost, vermi beds, or related products.</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-800 font-medium">Service Management</td>
                  <td className="py-3 px-4 text-gray-600">To manage your account, provide customer support, and respond to your inquiries.</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-800 font-medium">Communication</td>
                  <td className="py-3 px-4 text-gray-600">To send you order status updates, transactional messages, composting tips, and promotional materials (only if consented).</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-800 font-medium">Improvement & Development</td>
                  <td className="py-3 px-4 text-gray-600">To analyze usage trends and feedback to improve our products, services, and website functionality.</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-800 font-medium">Security</td>
                  <td className="py-3 px-4 text-gray-600">To detect, prevent, and address technical issues, fraud, or illegal activities.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* --- 3. Disclosure of Your Information --- */}
        <section className="mb-10 space-y-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-l-4 border-emerald-500 pl-3">
            3. Disclosure of Your Information
          </h2>
          <p className="text-gray-700">
            We do not sell, rent, or trade your personal information. We may disclose your information only under the following limited circumstances:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
            <li>
              Service Providers: We may share data with trusted third-party service providers who perform functions on our behalf, such as payment gateways and shipping carriers.
            </li>
            <li>
              Legal Requirements: If required to do so by law or in response to valid requests by public authorities (e.g., a court order or government agency).
            </li>
            <li>
              Business Transfer: In connection with any merger, sale of company assets, or acquisition of all or a portion of our business by another company.
            </li>
          </ul>
        </section>

        {/* --- 4. Data Security --- */}
        <section className="mb-10 space-y-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-l-4 border-emerald-500 pl-3">
            4. Data Security
          </h2>
          <p className="text-gray-700 leading-relaxed">
            We implement industry-standard security measures (such as SSL encryption) to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, please remember that no method of transmission over the Internet is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security.
          </p>
        </section>
        
        {/* --- 5. Your Data Protection Rights --- */}
        <section className="mb-10 space-y-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-l-4 border-emerald-500 pl-3">
            5. Your Data Protection Rights
          </h2>
          <p className="text-gray-700">
            You have the following rights regarding your personal information, subject to local law:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
            <li>Right of Access: The right to request a copy of the personal information we hold about you.</li>
            <li>Right to Rectification: The right to request that we correct any information you believe is inaccurate or incomplete.</li>
            <li>Right to Erasure (Deletion): The right to request that we delete your personal information under certain conditions.</li>
            <li>Right to Opt-Out: The right to unsubscribe or opt-out from our marketing communications at any time.</li>
          </ul>
        </section>
        
        {/* --- 6. Contact Us --- */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-l-4 border-emerald-500 pl-3">
            6. Contact Us
          </h2>
          <p className="text-gray-700">
            If you have any questions about this Privacy Policy or wish to exercise any of your rights, please contact us:
          </p>
          
          <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg space-y-2">
            <p className="text-gray-800">
              <span className="font-semibold">By Email:</span> <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>
            </p>
            <p className="text-gray-800">
              <span className="font-semibold">By Phone:</span> {CONTACT_PHONE}
            </p>
            <p className="text-gray-800">
              <span className="font-semibold">By Mail:</span> {CONTACT_ADDRESS}
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default PrivacyPolicyPage;